import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

// ── Animation knobs ─────────────────────────────────────────────────────────
const RESIZE_MS = 300;
const MOVE_MS = 450;
const MORPH_MS = RESIZE_MS + MOVE_MS;
const MORPH_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
// When (along the flight) the source content has fully faded out / the destination
// content starts fading in. On exit the destination is the big card, which looks
// strange clipped early — so it stays empty until the box is ~80% grown.
const SOURCE_OUT = 0.45;
const DEST_IN = 0.5;
const EXIT_DEST_IN = 0.8;

function morphKeyframes(
  from: DOMRect,
  to: DOMRect,
  fromRadius: string,
  toRadius: string,
  order: "resize-then-move" | "move-then-resize",
): Keyframe[] {
  const start: Keyframe = {
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    borderRadius: fromRadius,
    offset: 0,
    easing: MORPH_EASING,
  };
  const end: Keyframe = {
    left: `${to.left}px`,
    top: `${to.top}px`,
    width: `${to.width}px`,
    height: `${to.height}px`,
    borderRadius: toRadius,
    offset: 1,
  };

  if (order === "resize-then-move") {
    const midLeft = from.left + (from.width - to.width) / 2;
    const mid: Keyframe = {
      left: `${midLeft}px`,
      top: `${from.top}px`,
      width: `${to.width}px`,
      height: `${to.height}px`,
      borderRadius: toRadius,
      offset: RESIZE_MS / MORPH_MS,
      easing: MORPH_EASING,
    };
    return [start, mid, end];
  }

  const midLeft = to.left + (to.width - from.width) / 2;
  const mid: Keyframe = {
    left: `${midLeft}px`,
    top: `${to.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    borderRadius: fromRadius,
    offset: MOVE_MS / MORPH_MS,
    easing: MORPH_EASING,
  };
  return [start, mid, end];
}

/**
 * Card <-> nav-pill morph (container transform), content decoupled from the box.
 *
 * The layout flips immediately, so the card/input/real-pill always FLIP from their
 * correct final layout. A blank, fixed `flyer` box does the visible travel/resize.
 * The CONTENT is NOT inside the flyer — it lives in two fixed overlays pinned to the
 * real endpoint rectangles (card rect, pill rect). They never move or reflow; only
 * their opacity changes: the source content fades out, then (blank box in between)
 * the destination content fades in exactly where the real element will sit.
 *
 * All animations are tracked and cancelled before a new flight so rapid in/out
 * triggering stays consistent (no compounding / speed-up).
 */
export function useMorphTransition() {
  const [writing, setWriting] = useState(false);
  const [flightHidden, setFlightHidden] = useState<"card" | "pill" | null>(null);

  const cardRef = useRef<HTMLElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const flyerRef = useRef<HTMLDivElement>(null);
  const cardLayerRef = useRef<HTMLDivElement>(null);
  const pillLayerRef = useRef<HTMLDivElement>(null);
  const pendingRef = useRef<{ dir: "in" | "out"; from: DOMRect; inputFrom: DOMRect | null } | null>(null);
  const activeAnimsRef = useRef<Animation[]>([]);

  const resetVisuals = useCallback(() => {
    const flyer = flyerRef.current;
    if (flyer) flyer.style.opacity = "0";
    for (const layer of [cardLayerRef.current, pillLayerRef.current]) {
      if (!layer) continue;
      layer.style.opacity = "0";
      layer.style.width = "";
      layer.style.height = "";
    }
  }, []);

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;

    const enter = pending.dir === "in";
    const destEl = enter ? pillRef.current : cardRef.current;
    const flyer = flyerRef.current;
    if (!destEl || !flyer) {
      setFlightHidden(null);
      return;
    }

    // Cancel leftovers from a previous (rapid) trigger so flights never compound.
    activeAnimsRef.current.forEach((a) => a.cancel());
    activeAnimsRef.current = [];

    const from = pending.from;
    const to = destEl.getBoundingClientRect();
    const [r0, r1] = enter ? ["36px", "999px"] : ["999px", "36px"];
    const order = enter ? "resize-then-move" : "move-then-resize";

    setFlightHidden(enter ? "pill" : "card");

    // Blank box travels + resizes.
    flyer.style.opacity = "1";
    const boxAnim = flyer.animate(morphKeyframes(from, to, r0, r1, order), { duration: MORPH_MS, fill: "both" });
    activeAnimsRef.current.push(boxAnim);

    // Content overlays pinned to the real endpoint rects (never move/reflow).
    const cardLayer = cardLayerRef.current;
    const pillLayer = pillLayerRef.current;
    const cardSide = enter ? from : to;
    const pillSide = enter ? to : from;
    if (cardLayer) {
      cardLayer.style.width = `${cardSide.width}px`;
      cardLayer.style.height = `${cardSide.height}px`;
    }
    if (pillLayer) {
      pillLayer.style.width = `${pillSide.width}px`;
      pillLayer.style.height = `${pillSide.height}px`;
    }

    const sourceLayer = enter ? cardLayer : pillLayer;
    const destLayer = enter ? pillLayer : cardLayer;
    if (sourceLayer) {
      activeAnimsRef.current.push(
        sourceLayer.animate(
          [
            { opacity: 1, offset: 0 },
            { opacity: 0, offset: SOURCE_OUT },
            { opacity: 0, offset: 1 },
          ],
          { duration: MORPH_MS, easing: "ease", fill: "both" },
        ),
      );
    }
    if (destLayer) {
      const destIn = enter ? DEST_IN : EXIT_DEST_IN;
      activeAnimsRef.current.push(
        destLayer.animate(
          [
            { opacity: 0, offset: 0 },
            { opacity: 0, offset: destIn },
            { opacity: 1, offset: 1 },
          ],
          { duration: MORPH_MS, easing: "ease", fill: "both" },
        ),
      );
    }

    boxAnim.onfinish = () => {
      // Reveal the real element and hide the flyer in the SAME frame (flushSync
      // commits the reveal synchronously), so the two glass layers never overlap —
      // that one-frame overlap was the end "flash".
      flushSync(() => setFlightHidden(null));
      flyer.style.opacity = "0";
      activeAnimsRef.current.forEach((a) => a.cancel());
      activeAnimsRef.current = [];
      resetVisuals();
    };

    // Input FLIP — element is in its final layout, so old-rect → new-rect is correct
    // both directions. Active window matches the flyer's MOVE phase.
    const inputEl = inputRef.current;
    if (inputEl && pending.inputFrom) {
      const newRect = inputEl.getBoundingClientRect();
      const old = pending.inputFrom;
      const dy = old.top - newRect.top;
      const moveStart = enter ? RESIZE_MS / MORPH_MS : 0;
      const moveEnd = enter ? 1 : MOVE_MS / MORPH_MS;
      activeAnimsRef.current.push(
        inputEl.animate(
          [
            { transform: `translateY(${dy}px)`, height: `${old.height}px`, offset: 0, easing: MORPH_EASING },
            { transform: `translateY(${dy}px)`, height: `${old.height}px`, offset: moveStart, easing: MORPH_EASING },
            { transform: "translateY(0px)", height: `${newRect.height}px`, offset: moveEnd, easing: MORPH_EASING },
            { transform: "translateY(0px)", height: `${newRect.height}px`, offset: 1 },
          ],
          { duration: MORPH_MS },
        ),
      );
    }
  }, [writing, resetVisuals]);

  const enterWriting = () => {
    const cardEl = cardRef.current;
    const inputFrom = inputRef.current?.getBoundingClientRect() ?? null;
    pendingRef.current = cardEl ? { dir: "in", from: cardEl.getBoundingClientRect(), inputFrom } : null;
    setWriting(true);
  };

  const exitWriting = () => {
    const pillEl = pillRef.current;
    const inputFrom = inputRef.current?.getBoundingClientRect() ?? null;
    pendingRef.current = pillEl ? { dir: "out", from: pillEl.getBoundingClientRect(), inputFrom } : null;
    setWriting(false);
  };

  return {
    writing,
    flightHidden,
    cardRef,
    pillRef,
    inputRef,
    flyerRef,
    cardLayerRef,
    pillLayerRef,
    enterWriting,
    exitWriting,
  };
}
